import {
  REQUEST_CONTEXT_CONSENT_GROUPS,
  REQUEST_CONTEXT_COOKIE_ATTRIBUTES,
  REQUEST_CONTEXT_COOKIE_LIFETIME,
  REQUEST_CONTEXT_COOKIE_MAX_BYTES,
  REQUEST_CONTEXT_COOKIE_NAME,
  REQUEST_CONTEXT_COOKIE_PREFIX,
  REQUEST_CONTEXT_EXPERIMENT_PATTERN,
  REQUEST_CONTEXT_PARTNER_TRIP_ID_PATTERN,
  REQUEST_CONTEXT_REJECTED_COOKIE_MARKERS,
  REQUEST_CONTEXT_SESSION_COOKIE_WORKING_NAME,
  REQUEST_CONTEXT_UNKNOWN,
  UNKNOWN_REQUEST_CONTEXT,
  writeRequestContextCookie,
  type RequestContextSnapshot,
} from './contract';
import type { RequestContextClientMapping } from './mapping';

/**
 * Shared fixtures. Example values follow the platform field rules. The bot
 * field is the 1–99 score. A verified-bot header is not copied onto it.
 *
 * OneTrust reads its own cookie. A missing cookie leaves `consent` unknown.
 */

const exampleSnapshot = {
  partnerTripId: '0b8e6c9a-2f3d-4e8b-9a71-5d0c2e4f7a13',
  sessionState: 'signed-in',
  experiment: 'rv',
  bot: '87',
  consent: 'C0001,C0002,C0003,C0004,C0005',
} satisfies RequestContextSnapshot;

/**
 * Sample client record. Header and cookie names belong to the record.
 * They are not platform field names. The visit id comes from an edge
 * header. A browser cookie is not that source.
 */
export const examplePartnerRequestContextMapping = {
  partnerId: 'example-partner',
  fields: [
    {
      field: 'partnerTripId',
      source: { kind: 'header', name: 'x-acme-visit' },
      trust: 'partner-edge',
      transform: {
        type: 'pattern',
        pattern: REQUEST_CONTEXT_PARTNER_TRIP_ID_PATTERN,
      },
    },
    {
      field: 'sessionState',
      source: { kind: 'cookie', name: 'acme_session' },
      trust: 'browser',
      transform: {
        type: 'valueMap',
        values: { member: 'signed-in', guest: 'signed-out' },
      },
    },
    {
      field: 'experiment',
      source: { kind: 'header', name: 'x-acme-group' },
      trust: 'partner-edge',
      transform: {
        type: 'pattern',
        pattern: REQUEST_CONTEXT_EXPERIMENT_PATTERN,
      },
    },
    {
      field: 'bot',
      source: { kind: 'header', name: 'x-acme-automated' },
      trust: 'partner-edge',
      transform: {
        type: 'valueMap',
        values: { yes: '1', no: '99' },
      },
    },
    {
      field: 'consent',
      source: { kind: 'cookie', name: 'acme_groups' },
      trust: 'browser',
      transform: {
        type: 'oneTrustGroup',
        groups: REQUEST_CONTEXT_CONSENT_GROUPS,
      },
    },
  ],
} as const satisfies RequestContextClientMapping;

export const requestContextFixtures = {
  unknown: UNKNOWN_REQUEST_CONTEXT,
  cookie: {
    name: REQUEST_CONTEXT_COOKIE_NAME,
    prefix: REQUEST_CONTEXT_COOKIE_PREFIX,
    lifetime: REQUEST_CONTEXT_COOKIE_LIFETIME,
    ...REQUEST_CONTEXT_COOKIE_ATTRIBUTES,
    maxBytes: REQUEST_CONTEXT_COOKIE_MAX_BYTES,
    sessionCookieWorkingName: REQUEST_CONTEXT_SESSION_COOKIE_WORKING_NAME,
    rejectedMarkers: REQUEST_CONTEXT_REJECTED_COOKIE_MARKERS,
  },
  exampleHeaders: {
    'x-edu-ctx-partner-trip-id': exampleSnapshot.partnerTripId,
    'x-edu-ctx-session-state': exampleSnapshot.sessionState,
    'x-edu-ctx-experiment': exampleSnapshot.experiment,
    'x-edu-ctx-bot': exampleSnapshot.bot,
    'x-edu-ctx-consent': exampleSnapshot.consent,
  },
  exampleSnapshot,
  exampleCookie: writeRequestContextCookie(exampleSnapshot),
  blankHeaders: {
    'x-edu-ctx-partner-trip-id': '   ',
    'x-edu-ctx-session-state': '',
  },
  unknownToken: REQUEST_CONTEXT_UNKNOWN,
  /**
   * A missing consent cookie stays unknown. A present cookie that grants
   * none of the configured groups stays unknown until a denied value is
   * named. OneTrust reads the browser cookie itself.
   */
  consent: {
    missingCookieReadsAs: REQUEST_CONTEXT_UNKNOWN,
    noGrantedGroupsReadsAs: REQUEST_CONTEXT_UNKNOWN,
  },
  partners: {
    examplePartner: examplePartnerRequestContextMapping,
  },
} as const;
